/**
 * Consolidado de presupuestos (owner/admin): consumo por cliente con semáforo,
 * costo interno y margen, más descargas por cliente y export consolidado.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Download, FileSpreadsheet, FileText, Scale } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ConsumptionBar } from "@/components/feature/media/consumption-bar";
import { useMe } from "@/hooks/use-me";
import { useActiveClient } from "@/hooks/use-active-client";
import { media } from "@/lib/api";

export const Route = createFileRoute("/_auth/media/budgets")({
  component: MediaBudgets,
});

function money(currency: string, v: number) {
  return `${currency} ${v.toLocaleString("es-PE", { minimumFractionDigits: 2 })}`;
}

function MediaBudgets() {
  const { data: me } = useMe();
  const isAdmin = me?.role === "owner" || me?.role === "admin";
  const { activeClientId } = useActiveClient();
  const { data, isLoading } = useQuery({
    queryKey: ["media", "budgets", "overview"],
    queryFn: () => media.budgetsOverview(),
    enabled: isAdmin,
  });

  if (me && !isAdmin) {
    return (
      <div className="text-center py-12 text-ink-500 border-2 border-dashed border-ink-200 rounded-lg">
        Sin permisos para ver el consolidado de presupuestos.
      </div>
    );
  }

  // Respeta el selector "Cliente activo" del topbar (filtro client-side:
  // el overview ya trae todos los clientes de la cuenta).
  const allRows = data?.rows ?? [];
  const rows = activeClientId != null
    ? allRows.filter((r) => r.client_id === activeClientId)
    : allRows;
  // Con un solo cliente filtrado los totales por moneda son redundantes.
  const totals = activeClientId == null ? (data?.totals_by_currency ?? {}) : {};

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-ink-500">
          {activeClientId != null
            ? "Filtrado por cliente activo · consumo comprometido (precio facturado)"
            : `${rows.length} clientes · consumo comprometido (precio facturado)`}
        </div>
        <Button asChild>
          <a href={media.budgetsOverviewExportXlsxUrl()} download>
            <Download size={14} /> Exportar consolidado
          </a>
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <Card key={r.client_id}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="min-w-0 flex items-center gap-2">
                    <Link
                      to="/clients/$clientId"
                      params={{ clientId: String(r.client_id) }}
                      className="font-semibold truncate hover:text-brand-600"
                    >
                      {r.client_name}
                    </Link>
                    <span className="text-xs text-ink-400">({r.client_code})</span>
                    {!r.has_budget && <Badge variant="outline">Sin presupuesto</Badge>}
                    {r.cost_missing > 0 && (
                      <Badge className="bg-amber-100 text-amber-800" title="Inserciones comprometidas sin costo interno registrado — el margen está incompleto">
                        {r.cost_missing} sin costo
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Presupuesto Excel" asChild>
                      <a href={media.budgetExportXlsxUrl(r.client_id)} download><FileSpreadsheet size={15} /></a>
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Presupuesto PDF" asChild>
                      <a href={media.budgetExportPdfUrl(r.client_id)} download><FileText size={15} /></a>
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Conciliación (costo/precio)" asChild>
                      <a href={media.conciliationExportXlsxUrl(r.client_id)} download><Scale size={15} /></a>
                    </Button>
                  </div>
                </div>

                <ConsumptionBar
                  label={r.has_budget && r.budget_total != null ? "Consumido / Presupuesto" : "Consumido"}
                  amount={r.budget_total}
                  spent={r.spent}
                  pct={r.pct}
                  currency={r.currency}
                />

                <div className="flex items-center gap-4 mt-2 text-xs text-ink-500">
                  <span>Costo interno: <span className="font-medium text-ink-700 dark:text-ink-200">{money(r.currency, r.cost)}</span></span>
                  <span>Margen: <span className={`font-medium ${r.margin < 0 ? "text-rose-600" : "text-emerald-600"}`}>{money(r.currency, r.margin)}</span></span>
                </div>
              </CardContent>
            </Card>
          ))}

          {!rows.length && (
            <div className="text-center py-12 text-ink-500 border-2 border-dashed border-ink-200 rounded-lg">
              {activeClientId != null
                ? "El cliente activo no aparece en el consolidado (¿está inactivo?)."
                : "No hay clientes activos."}
            </div>
          )}

          {Object.keys(totals).length > 0 && (
            <Card className="border-brand-200">
              <CardContent className="py-4">
                <div className="text-[10px] uppercase tracking-widest text-ink-400 mb-2">Totales por moneda</div>
                <div className="space-y-1">
                  {Object.entries(totals).map(([cur, t]) => (
                    <div key={cur} className="flex flex-wrap items-center gap-4 text-sm">
                      <span className="font-semibold w-10">{cur}</span>
                      <span className="text-ink-500 text-xs">Presupuesto: <span className="font-medium text-ink-700 dark:text-ink-200">{money(cur, t.budget_total)}</span></span>
                      <span className="text-ink-500 text-xs">Consumido: <span className="font-medium text-ink-700 dark:text-ink-200">{money(cur, t.spent)}</span></span>
                      <span className="text-ink-500 text-xs">Costo: <span className="font-medium text-ink-700 dark:text-ink-200">{money(cur, t.cost)}</span></span>
                      <span className="text-ink-500 text-xs">Margen: <span className={`font-medium ${t.margin < 0 ? "text-rose-600" : "text-emerald-600"}`}>{money(cur, t.margin)}</span></span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </>
  );
}
